import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { RideDTO } from '@/api/generated/types';
import { Badge, Tooltip, type Tone } from '@/ui';

// RideDTO.violateType is the vehicle's parking fence status (veo-ride constant.FenceStatus*); the
// legacy console mislabelled 1 and 4 ("In restricted fence", "unknown").
const PARK_NPZ = 1; // inside a no-parking zone
const PARK_OUT_OF_GEOFENCE = 2; // out of the service area
const PARK_OUT_OF_FPZ = 3; // outside the forced parking zone
const PARK_NRZ = 4; // inside a no-ride zone
const PARK_START_NRZ = 6; // inside the no-ride zone the ride started in
const PARK_UNKNOWN = -1; // constant.GeneralTypeUnknown
// RideDTO.limitType is the vehicle's speed status (constant.SpeedStatus*).
const SPEED_NO_SPEED = 1; // stopped: in a no-ride zone, or out of the geofence
const SPEED_SLOW = 2;
const SPEED_SIDEWALK = 3;
/** Vehicle types whose rides report a speed status (legacy: Astro, Cosmo, Apollo, Trike). */
const SPEED_VEHICLE_TYPES = [1, 3, 4, 5];

export type ZoneTagKey =
  | 'parkingNormal'
  | 'ridingNormal'
  | 'nrzOutOfGeofence'
  | 'nrz'
  | 'nrzStartZone'
  | 'slow'
  | 'sidewalk'
  | 'npz'
  | 'outOfGeofence'
  | 'outOfFpz'
  | 'startedInNrz'
  | 'unknown';

const TONE: Record<ZoneTagKey, Tone> = {
  parkingNormal: 'neutral',
  ridingNormal: 'neutral',
  nrzOutOfGeofence: 'danger',
  nrz: 'danger',
  nrzStartZone: 'warning',
  npz: 'danger',
  outOfGeofence: 'danger',
  outOfFpz: 'warning', // never the plan badges' blue: violations are red or amber
  slow: 'warning',
  sidewalk: 'warning',
  startedInNrz: 'warning',
  unknown: 'neutral',
};

type ZoneFields = Pick<RideDTO, 'violateType' | 'limitType' | 'vehicleType' | 'startRideInNrz'>;

/**
 * In a no-ride zone, by either field: the parking status says NRZ (4, or 6 for the zone the ride
 * started in), or the speed is cut to 0 without the vehicle being out of the geofence.
 */
function inNrz(r: ZoneFields): boolean {
  if (r.violateType === PARK_NRZ || r.violateType === PARK_START_NRZ) return true;
  return speedStatus(r) === SPEED_NO_SPEED && r.violateType !== PARK_OUT_OF_GEOFENCE;
}

/** The speed status, for vehicle types that report one (legacy: Astro, Cosmo, Apollo, Trike). */
function speedStatus(r: ZoneFields): number | null | undefined {
  return r.vehicleType != null && SPEED_VEHICLE_TYPES.includes(r.vehicleType) ? r.limitType : null;
}

/**
 * Parking violation (violateType) only, as the legacy console showed it: a ride whose speed was cut
 * in an NRZ but that was parked legally reads Normal here and NRZ under Riding zone.
 */
export function parkingTags(r: ZoneFields, showNormal = false): ZoneTagKey[] {
  switch (r.violateType) {
    case 0:
      return showNormal ? ['parkingNormal'] : [];
    case PARK_NPZ:
      return ['npz'];
    case PARK_OUT_OF_GEOFENCE:
      return ['outOfGeofence'];
    case PARK_OUT_OF_FPZ:
      return ['outOfFpz'];
    case PARK_NRZ:
      return ['nrz'];
    case PARK_START_NRZ:
      return ['nrzStartZone'];
    case PARK_UNKNOWN:
      return ['unknown'];
  }
  return [];
}

/**
 * Riding zone (limitType): what the vehicle is held to while riding. "No speed" out of the geofence
 * is NRZ out of geofence, told apart from an NRZ proper.
 */
export function ridingZoneTags(r: ZoneFields, showNormal = false): ZoneTagKey[] {
  const tags: ZoneTagKey[] = [];
  const speed = speedStatus(r);
  if (speed === SPEED_NO_SPEED && r.violateType === PARK_OUT_OF_GEOFENCE) tags.push('nrzOutOfGeofence');
  else if (inNrz(r)) tags.push('nrz');
  else if (showNormal && speed === 0) tags.push('ridingNormal');
  if (speed === SPEED_SLOW) tags.push('slow');
  if (speed === SPEED_SIDEWALK) tags.push('sidewalk');
  if (r.startRideInNrz?.startRideInNrz) tags.push('startedInNrz');
  return tags;
}

/** Both, deduplicating shared violations; the legacy status view puts parking before riding. */
export function rideZoneTags(r: ZoneFields, showNormal = false): ZoneTagKey[] {
  const riding = ridingZoneTags(r, showNormal);
  const parking = parkingTags(r, showNormal).filter(
    (k) => !(k === 'nrz' && riding.includes('nrz')) && !(k === 'outOfGeofence' && riding.includes('nrzOutOfGeofence')),
  );
  return showNormal ? [...parking, ...riding] : [...riding, ...parking];
}

/** Zone badges; the list opts into explicit Normal statuses, while summaries show issues only. */
export function RideZoneTags({ ride, empty = null, only, stacked = false, showNormal = false }: { ride: ZoneFields; empty?: ReactNode; only?: 'parking' | 'riding'; stacked?: boolean; showNormal?: boolean }) {
  const { t } = useTranslation('rides', { keyPrefix: 'zoneTags' });
  const tags = only === 'parking' ? parkingTags(ride, showNormal) : only === 'riding' ? ridingZoneTags(ride, showNormal) : rideZoneTags(ride, showNormal);
  if (!tags.length) return <>{empty}</>;
  return (
    <span className={stacked ? 'inline-flex flex-col items-start gap-1' : 'inline-flex flex-wrap gap-1'}>
      {tags.map((k) => (
        <Tooltip key={k} content={t(`hint.${k}`)}>
          <span>
            <Badge tone={TONE[k]}>{t(k)}</Badge>
          </span>
        </Tooltip>
      ))}
    </span>
  );
}
